import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useCreateClient, CreateClientPayload } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";

interface FormValues {
  clientType: "PERSONA_NATURAL" | "PERSONA_JURIDICA";
  documentType: "CC" | "CE" | "NIT" | "PASAPORTE" | "TI";
  documentNumber: string;
  fullName?: string;
  businessName?: string;
  tradeName?: string;
  email?: string;
  phone?: string;
  city?: string;
}

export default function CreateClientModal({ onClose }: { onClose: () => void }) {
  const { register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { clientType: "PERSONA_NATURAL", documentType: "CC" },
  });
  const createClient = useCreateClient();
  const navigate = useNavigate();
  const clientType = watch("clientType");

  const onSubmit = async (values: FormValues) => {
    try {
      const client = await createClient.mutateAsync(values as CreateClientPayload);
      toast.success("Cliente creado correctamente.");
      onClose();
      navigate(`/clients/${client.id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900">Nuevo cliente</h2>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="client-type" className="block text-sm font-medium text-gray-700">
              Tipo de cliente
            </label>
            <select id="client-type" className="input-field mt-1" {...register("clientType")}>
              <option value="PERSONA_NATURAL">Persona natural</option>
              <option value="PERSONA_JURIDICA">Persona jurídica</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="client-document-type" className="block text-sm font-medium text-gray-700">
                Tipo de documento
              </label>
              <select id="client-document-type" className="input-field mt-1" {...register("documentType")}>
                <option value="CC">Cédula de ciudadanía</option>
                <option value="CE">Cédula de extranjería</option>
                <option value="NIT">NIT</option>
                <option value="PASAPORTE">Pasaporte</option>
                <option value="TI">Tarjeta de identidad</option>
              </select>
            </div>
            <div>
              <label htmlFor="client-document-number" className="block text-sm font-medium text-gray-700">
                Número de documento
              </label>
              <input
                id="client-document-number"
                className="input-field mt-1"
                {...register("documentNumber", { required: true })}
              />
            </div>
          </div>

          {clientType === "PERSONA_NATURAL" ? (
            <div>
              <label htmlFor="client-full-name" className="block text-sm font-medium text-gray-700">
                Nombre completo
              </label>
              <input
                id="client-full-name"
                className="input-field mt-1"
                {...register("fullName", { required: clientType === "PERSONA_NATURAL" })}
              />
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="client-business-name" className="block text-sm font-medium text-gray-700">
                  Razón social
                </label>
                <input
                  id="client-business-name"
                  className="input-field mt-1"
                  {...register("businessName", { required: clientType === "PERSONA_JURIDICA" })}
                />
              </div>
              <div>
                <label htmlFor="client-trade-name" className="block text-sm font-medium text-gray-700">
                  Nombre comercial
                </label>
                <input id="client-trade-name" className="input-field mt-1" {...register("tradeName")} />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="client-email" className="block text-sm font-medium text-gray-700">
                Correo electrónico
              </label>
              <input id="client-email" type="email" className="input-field mt-1" {...register("email")} />
            </div>
            <div>
              <label htmlFor="client-phone" className="block text-sm font-medium text-gray-700">
                Teléfono
              </label>
              <input id="client-phone" className="input-field mt-1" {...register("phone")} />
            </div>
          </div>

          <div>
            <label htmlFor="client-city" className="block text-sm font-medium text-gray-700">
              Ciudad
            </label>
            <input id="client-city" className="input-field mt-1" {...register("city")} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createClient.isPending}>
              {createClient.isPending ? "Creando..." : "Crear cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
